import type React from 'react';
import { Box, Paragraph } from '@zillow/constellation-10';
import { parseTextWithLinks } from '../../utils/parseTextWithLinks';
import { chatBubbleStyles } from '../ChatBubble/styles';
import { CHAT_BUBBLE_VARIANT_STYLES } from '../ChatBubble';

export type InteractiveChatBubbleProps = {
  children: React.ReactNode;
  senderName?: string;
  shouldIncludeGutter?: boolean;
  variant: 'autoResponse' | 'messageOwner' | 'default';
  onImpact?: boolean;
  onLinkClick?: (url: string, displayText?: string) => void;
};

export default function InteractiveChatBubble({
  children,
  senderName,
  shouldIncludeGutter = false,
  variant,
  onImpact = false,
  onLinkClick,
}: InteractiveChatBubbleProps) {
  // Process children to parse URLs if it's a string
  const processedChildren =
    typeof children === 'string' ? parseTextWithLinks(children, onImpact, onLinkClick) : children;
  const variantStyles = CHAT_BUBBLE_VARIANT_STYLES[variant];
  const isMessageOwner = variant === 'messageOwner';
  const shouldDisplaySenderName = (!!senderName && variant === 'default') || isMessageOwner;
  const senderNameText = isMessageOwner ? 'You' : senderName;
  const textColor = onImpact && isMessageOwner ? 'white' : 'text.neutral';

  return (
    <Box
      data-testid="interactive-chat-bubble"
      css={{
        ...chatBubbleStyles,
        ...variantStyles,
        ...(shouldIncludeGutter && {
          marginLeft: isMessageOwner ? 'layout.looser' : '0',
          marginRight: isMessageOwner ? '0' : 'layout.looser',
        }),
      }}
    >
      {shouldDisplaySenderName && (
        <Paragraph
          textStyle="body-bold"
          css={{ color: textColor, marginBottom: 'tighter', textTransform: 'capitalize' }}
        >
          {senderNameText}
        </Paragraph>
      )}
      <Paragraph css={{ color: textColor, wordBreak: 'break-word' }}>{processedChildren}</Paragraph>
    </Box>
  );
}
